import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stack,
  Slider,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Visibility as RevealIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getVerifications, decideVerification, fetchVerificationScreenshot } from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import type { VerificationWithUser } from '@/types';

interface ScreenshotPreviewProps {
  screenshotKey: string;
  title: string;
  onOpen: (src: string, title: string) => void;
}

function ScreenshotPreview({ screenshotKey, title, onOpen }: ScreenshotPreviewProps) {
  const { t } = useAdminI18n();
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;

    setSrc(null);
    setError(null);

    if (!revealed) {
      return () => {
        isMounted = false;
      };
    }

    fetchVerificationScreenshot(screenshotKey)
      .then((blob) => {
        if (!isMounted) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err: Error) => {
        if (!isMounted) return;
        setError(err.message);
      });

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [revealed, screenshotKey]);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!revealed) {
    return (
      <Box
        sx={{
          width: 378,
          maxWidth: '100%',
          minHeight: 138,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'rgba(244, 255, 249, 0.035)',
          backgroundImage:
            'linear-gradient(135deg, rgba(118, 244, 184, 0.08), rgba(255, 209, 102, 0.05))',
        }}
      >
        <Button variant="outlined" startIcon={<RevealIcon />} onClick={() => setRevealed(true)}>
          {t('admin.verifications.revealScreenshot')}
        </Button>
      </Box>
    );
  }

  if (!src) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          {t('admin.verifications.loadingScreenshot')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(src, title)}
      sx={{
        position: 'relative',
        display: 'block',
        p: 0,
        overflow: 'hidden',
        width: 'fit-content',
        maxWidth: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'rgba(244, 255, 249, 0.035)',
        cursor: 'zoom-in',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={t('admin.verifications.screenshotAlt')}
        sx={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: 300,
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          position: 'absolute',
          right: 8,
          bottom: 8,
          px: 1,
          py: 0.5,
          borderRadius: 999,
          color: '#06120D',
          bgcolor: 'rgba(244, 255, 249, 0.88)',
          fontWeight: 800,
        }}
      >
        <ZoomInIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 800 }}>
          {t('admin.verifications.view')}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function AdminVerifications() {
  const queryClient = useQueryClient();
  const { t } = useAdminI18n();
  const [statusFilter, setStatusFilter] = useState<'pending' | 'verified' | 'rejected'>('pending');
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [screenshotDialog, setScreenshotDialog] = useState<{ src: string; title: string } | null>(null);
  const [screenshotZoom, setScreenshotZoom] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-verifications', statusFilter],
    queryFn: () => getVerifications(statusFilter),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: number; status: 'verified' | 'rejected'; reason?: string }) =>
      decideVerification(id, status, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-verifications'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setRejectDialog({ open: false, id: null });
      setRejectionReason('');
    },
  });

  const handleApprove = (id: number) => {
    decideMutation.mutate({ id, status: 'verified' });
  };

  const handleOpenReject = (id: number) => {
    setRejectDialog({ open: true, id });
  };

  const handleReject = () => {
    if (rejectDialog.id) {
      decideMutation.mutate({ id: rejectDialog.id, status: 'rejected', reason: rejectionReason });
    }
  };

  const handleOpenScreenshot = (src: string, title: string) => {
    setScreenshotZoom(1);
    setScreenshotDialog({ src, title });
  };

  const statusLabels = {
    pending: t('admin.common.pending'),
    verified: t('admin.common.verified'),
    rejected: t('admin.common.rejected'),
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <SectionHeader
        title={t('admin.verifications.title')}
        detail={t('admin.verifications.detail', {
          count: data?.verifications.length ?? 0,
          status: statusLabels[statusFilter],
        })}
      />

      <Tabs
        value={statusFilter}
        onChange={(_, v) => setStatusFilter(v)}
        sx={{ mb: 2 }}
      >
        <Tab value="pending" label={t('admin.common.pending')} />
        <Tab value="verified" label={t('admin.common.verified')} />
        <Tab value="rejected" label={t('admin.common.rejected')} />
      </Tabs>

      {error ? (
        <Alert severity="error">{t('admin.verifications.loadError')}</Alert>
      ) : data?.verifications.length === 0 ? (
        <Alert severity="info">
          {t('admin.verifications.empty', { status: statusLabels[statusFilter] })}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data?.verifications.map((v: VerificationWithUser) => (
            <Card key={v.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {v.first_name} {v.last_name}
                      {v.telegram_username && (
                        <Typography component="span" color="text.secondary">
                          {' '}@{v.telegram_username}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('admin.verifications.telegramId', { id: v.telegram_id })}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
                      <ExchangeLogo src={v.logo_url} name={v.exchange_name} id={v.exchange_id} size={24} />
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.verifications.exchange', { exchange: v.exchange_name })}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.verifications.uid', { uid: v.exchange_uid })}
                    </Typography>
                    {v.rejection_reason && (
                      <Typography variant="body2" color="error">
                        {t('admin.verifications.rejection', { reason: v.rejection_reason })}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={statusLabels[v.verification_status]}
                    color={
                      v.verification_status === 'verified'
                        ? 'success'
                        : v.verification_status === 'rejected'
                        ? 'error'
                        : 'warning'
                    }
                    size="small"
                  />
                </Box>

                {v.screenshot_url && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('admin.verifications.screenshot')}
                    </Typography>
                    <ScreenshotPreview
                      screenshotKey={v.screenshot_url}
                      title={t('admin.verifications.screenshotFor', {
                        exchange: v.exchange_name,
                        name: v.first_name || t('admin.users.user'),
                      })}
                      onOpen={handleOpenScreenshot}
                    />
                  </Box>
                )}

                {statusFilter === 'pending' && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleApprove(v.id)}
                      disabled={decideMutation.isPending}
                    >
                      {t('admin.common.approve')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => handleOpenReject(v.id)}
                      disabled={decideMutation.isPending}
                    >
                      {t('admin.common.reject')}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null })}>
        <DialogTitle>{t('admin.verifications.rejectTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('admin.verifications.rejectionReason')}
            multiline
            rows={2}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, id: null })}>{t('admin.common.cancel')}</Button>
          <Button color="error" onClick={handleReject} disabled={decideMutation.isPending}>
            {t('admin.common.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(screenshotDialog)}
        onClose={() => setScreenshotDialog(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{screenshotDialog?.title || t('admin.verifications.screenshotTitle')}</DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            overflow: 'auto',
            bgcolor: '#050807',
            maxHeight: '78vh',
          }}
        >
          {screenshotDialog && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={screenshotDialog.src}
                alt={screenshotDialog.title}
                sx={{
                  width: `${screenshotZoom * 100}%`,
                  maxWidth: 'none',
                  minWidth: 320,
                  borderRadius: 1,
                  boxShadow: '0 20px 70px rgba(0, 0, 0, 0.42)',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 260 }}>
            <Button
              size="small"
              startIcon={<ZoomOutIcon />}
              onClick={() => setScreenshotZoom((value) => Math.max(1, value - 0.25))}
              disabled={screenshotZoom <= 1}
            >
              {t('admin.verifications.zoomOut')}
            </Button>
            <Slider
              min={1}
              max={3}
              step={0.25}
              value={screenshotZoom}
              onChange={(_, value) => setScreenshotZoom(value as number)}
              aria-label={t('admin.verifications.zoomAria')}
              sx={{ width: 150 }}
            />
            <Button
              size="small"
              startIcon={<ZoomInIcon />}
              onClick={() => setScreenshotZoom((value) => Math.min(3, value + 0.25))}
              disabled={screenshotZoom >= 3}
            >
              {t('admin.verifications.zoomIn')}
            </Button>
          </Stack>
          <Button onClick={() => setScreenshotDialog(null)}>{t('admin.common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
